import React, { useEffect, useMemo, useRef, useState } from "react";
import { fmtDateTimeFirm, fmtDate } from "@/lib/dt";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Clock, FileText, FolderOpen, AlertTriangle,
          Database, Users, History, ClipboardList, ShieldAlert, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { actionLabel, entityLabel, roleLabel, formTail, tcwgCategoryLabel } from "@/lib/w13Labels";
import { screenEntries, searchScreens } from "@/lib/screenSearch";   // Batch 28 · item 10 (M55)
import { asArray } from "@/lib/asArray";

// X1 (Round X) — bidi-stripped end-of-label detector, mirrors the
// helper in w13Labels.js so we can inline the tcwg_311a_log branch
// here without exporting the private function.
function _labelEndsWithRef(label, ref) {
  if (!label || !ref) return false;
  const clean = String(label).replace(/[\u2066\u2068\u2069]/g, "").trim().toLowerCase();
  return clean.endsWith(String(ref).toLowerCase());
}

// Icon+order metadata only. Labels resolve via t("searchBar.groups.*") at render.
const GROUP_META = {
  // Batch 27 · Add-on 3 · A (M40): the exact working-paper reference first, then the head-folder working papers
  best:              { key: "best",             Icon: Sparkles,      order: 0 },
  head_folders:      { key: "headFolders",      Icon: FolderOpen,    order: 0.1 },
  worksheets:        { key: "worksheets",       Icon: ClipboardList, order: 0.2 },
  procedures:        { key: "procedures",       Icon: ClipboardList, order: 0.3 },
  observations:      { key: "observations",     Icon: AlertTriangle, order: 0.4 },
  screens:           { key: "screens",          Icon: ArrowRight,    order: 0.5 },
  misstatements:     { key: "misstatements",    Icon: AlertTriangle, order: 9.1 },
  adjusting_entries: { key: "adjustingEntries", Icon: Database,      order: 9.2 },
  confirmations:     { key: "confirmations",    Icon: FileText,      order: 9.3 },
  kams:              { key: "kams",             Icon: FileText,      order: 9.4 },
  "forms.planning":              { key: "planning",     Icon: FolderOpen,     order: 1 },
  "forms.risk_assessment_forms": { key: "riskForms",    Icon: FolderOpen,     order: 2 },
  "forms.completion":            { key: "completion",   Icon: FolderOpen,     order: 3 },
  "forms.acceptance":            { key: "acceptance",   Icon: FolderOpen,     order: 0.9 },    // Batch 28 · item 10
  "forms.controls":              { key: "controlsForms", Icon: FolderOpen,    order: 3.1 },
  "forms.confirmations_forms":   { key: "confirmationsForms", Icon: FolderOpen, order: 3.2 },
  "forms.specialized_and_other_considerations": { key: "specialized", Icon: FolderOpen, order: 3.3 },
  "forms.audit_response":        { key: "auditResponse", Icon: FolderOpen,    order: 3.4 },
  gl:            { key: "gl",            Icon: Database,      order: 4 },
  documents:     { key: "documents",     Icon: FileText,      order: 5 },
  discrepancies: { key: "discrepancies", Icon: AlertTriangle, order: 6 },
  samples:       { key: "samples",       Icon: Sparkles,      order: 7 },
  vouching:      { key: "vouching",      Icon: ClipboardList, order: 8 },
  risk:          { key: "risk",          Icon: ShieldAlert,   order: 9 },
  team:          { key: "team",          Icon: Users,         order: 10 },
  audit_trail:   { key: "auditTrail",    Icon: History,       order: 11 },
};

const TYPE_FILTERS = [
  { value: "all",           i18nKey: "all" },
  { value: "forms",         i18nKey: "forms" },
  { value: "gl",            i18nKey: "gl" },
  { value: "documents",     i18nKey: "documents" },
  { value: "discrepancies", i18nKey: "discrepancies" },
  { value: "samples",       i18nKey: "samples" },
  { value: "vouching",      i18nKey: "vouching" },
  { value: "risk",          i18nKey: "risk" },
  { value: "team",          i18nKey: "team" },
  { value: "audit_trail",   i18nKey: "auditTrail" },
  { value: "head_folders",  i18nKey: "headFolders" },
  { value: "worksheets",    i18nKey: "worksheets" },
  { value: "procedures",    i18nKey: "procedures" },
  { value: "observations",  i18nKey: "observations" },
  { value: "misstatements", i18nKey: "misstatements" },
  { value: "adjusting_entries", i18nKey: "adjustingEntries" },
  { value: "confirmations", i18nKey: "confirmations" },
  { value: "kams",          i18nKey: "kams" },
  { value: "screens",       i18nKey: "screens" },
];

const RECENT_KEY = (engId) => `af_recent_search_${engId || "none"}`;
const RECENT_MAX = 5;

function Highlighted({ text, q }) {
  if (!text) return null;
  if (!q) return <>{text}</>;
  const parts = [];
  const rx = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const s = String(text);
  let last = 0;
  s.replace(rx, (match, _p, idx) => {
    if (idx > last) parts.push(s.slice(last, idx));
    parts.push(<mark key={parts.length} className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-800">{match}</mark>);
    last = idx + match.length;
    return match;
  });
  if (last < s.length) parts.push(s.slice(last));
  return <>{parts}</>;
}

export default function SearchBar({ navGroups = [], isReview: isReviewProp } = {}) {
  const { current, currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  // Batch 28 · item 10 (M55): the sidebar's own pages, matched in the browser as the user types (no list of our own)
  const screenList = useMemo(() => screenEntries(navGroups, { isReview: !!isReviewProp }), [navGroups, isReviewProp]);
  const isReview = (current?.engagement_type || "audit") === "review";
  const nav = useNavigate();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [recent, setRecent] = useState([]);
  const [formsIndex, setFormsIndex] = useState({});

  // V1 — fetch the forms library once so audit-trail form / engagement_forms
  // hits show the library ref + AR/EN title (same helper as W13).
  useEffect(() => {
    if (!currentEngId) return;
    api.get(`/forms/library`, { params: { engagement_id: currentEngId },
                                 _silent: true })
       .then(r => {
         const idx = {};
         for (const f of (r.data?.forms || [])) {
           if (f?.formId) idx[f.formId] = f;
         }
         setFormsIndex(idx);
       })
       .catch(() => setFormsIndex({}));
  }, [currentEngId]);

  // Load recent when engagement changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY(currentEngId));
      setRecent(raw ? JSON.parse(raw) : []);
    } catch { setRecent([]); }
    setQ(""); setResults(null);
  }, [currentEngId]);

  // Cmd+K / Ctrl+K focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Click outside closes panel
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!currentEngId) { setResults(null); return; }
    if (!q || q.trim().length < 2) { setResults(null); return; }
    let cancel = false;
    setBusy(true);
    const handle = setTimeout(() => {
      api.get(`/search/${currentEngId}`, { params: { q, type: typeFilter, limit: 20 } })
        .then((r) => { if (!cancel) setResults(r.data); })
        .catch(() => { if (!cancel) setResults({ query: q, total: 0, groups: {} }); })
        .finally(() => { if (!cancel) setBusy(false); });
    }, 250);
    return () => { cancel = true; clearTimeout(handle); };
  }, [q, typeFilter, currentEngId]);

  const localScreens = useMemo(() => (["all", "screens"].includes(typeFilter) ? searchScreens(q, screenList) : []), [q, screenList, typeFilter]);
  const groups = useMemo(() => {
    const g = { ...(results?.groups || {}) };
    // owner (Batch 28): uploaded files are not suggested — they stay under the Documents chip
    if (typeFilter === "all") delete g.documents;
    if (localScreens.length) {
      const seen = new Set(localScreens.map((h) => h.route));
      g.screens = [...localScreens, ...asArray(g.screens).filter((h) => !seen.has(h.route) || Object.keys(h.params || {}).length)];
    }
    if (!Object.keys(g).length) return [];
    return Object.entries(g)
      .map(([key, hits]) => {
        const m = GROUP_META[key] || { key: null, Icon: FileText, order: 99 };
        const label = m.key ? t(`globalSearch.groups.${m.key}`) : key;
        return { key, meta: { ...m, label }, hits };
      })
      .sort((a, b) => a.meta.order - b.meta.order);
  }, [results, t, localScreens, typeFilter]);

  const pushRecent = (term) => {
    if (!term || term.length < 2) return;
    const next = [term, ...recent.filter((r) => r !== term)].slice(0, RECENT_MAX);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY(currentEngId), JSON.stringify(next)); } catch {}
  };

  const goto = (hit, useItem = false) => {
    pushRecent(q);
    setOpen(false);
    const params = new URLSearchParams();
    const route = useItem && hit.item_route ? hit.item_route : hit.route;
    for (const [k, v] of Object.entries((useItem && hit.item_params) || hit.params || {})) {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    }
    const qs = params.toString();
    nav(route + (qs ? `?${qs}` : ""));
  };

  if (!currentEngId) return null;

  return (
    <div ref={wrapRef} className="app-search relative w-96" data-testid="global-search-wrap">
      <div className="relative">
        <Search className="pointer-events-none absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && q.trim().length >= 2) pushRecent(q.trim());
          }}
          placeholder={t("globalSearch.placeholder")}
          className="h-9 ps-8 pe-14 text-sm"
          data-testid="global-search-input"
        />
        <div className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
          {busy ? "…" : (q ? "" : "⌘K")}
        </div>
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setResults(null); inputRef.current?.focus(); }}
            className="absolute end-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-900"
            data-testid="global-search-clear"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {open && (
        <div
          className="absolute end-0 top-11 z-50 w-[560px] max-w-[92vw] rounded-lg border bg-white shadow-xl dark:bg-slate-900 dark:border-slate-800"
          data-testid="global-search-panel"
        >
          {/* Type filter chips */}
          <div className="flex flex-wrap gap-1 border-b bg-slate-50 px-3 py-2 text-[11px] dark:bg-slate-900/50">
            {TYPE_FILTERS.map((f) => (
              <button key={f.value}
                       onClick={() => setTypeFilter(f.value)}
                       data-testid={`search-filter-${f.value}`}
                       className={`rounded-full border px-2 py-0.5 transition
                         ${typeFilter === f.value
                           ? "border-emerald-600 bg-emerald-600 text-white"
                           : "border-slate-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700"}`}>
                {t(`globalSearch.filters.${f.i18nKey}`)}
              </button>
            ))}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {/* Empty & focused → recent searches */}
            {(!q || q.trim().length < 2) && (
              <div className="p-3 text-[12px]" data-testid="search-recent">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("globalSearch.recentSearches")}</div>
                {recent.length === 0 ? (
                  <div className="text-muted-foreground">{t("globalSearch.startTyping")}</div>
                ) : (
                  <ul className="space-y-1">
                    {recent.map((r, i) => (
                      <li key={i}>
                        <button className="flex w-full items-center gap-2 rounded px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-800"
                                 onClick={() => { setQ(r); inputRef.current?.focus(); }}
                                 data-testid={`search-recent-${i}`}>
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span>{r}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Results */}
            {q.trim().length >= 2 && (results || localScreens.length > 0) && (
              <>
                {groups.length === 0 && !busy && (
                  <div className="p-6 text-center text-[13px] text-muted-foreground" data-testid="search-no-results">
                    {t("globalSearch.noMatches").replace("{q}", q)}
                  </div>
                )}
                {groups.map(({ key, meta, hits }) => (
                  <div key={key} className="border-b last:border-b-0"
                        data-testid={`search-group-${key}`}>
                    <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 dark:bg-slate-900/60">
                      <meta.Icon className="h-3 w-3 text-emerald-500" />
                      <span>{meta.label}</span>
                      <Badge variant="outline" className="ms-auto text-[10px]">{hits.length}</Badge>
                    </div>
                    <ul>
                      {hits.map((h, i) => {
                        // V1 — audit-trail hits render via the same
                        // helpers as W13. Nothing pre-baked lives in
                        // the server payload.
                        let title, subtitle, snippet;
                        if (h.is_audit_trail) {
                          title = actionLabel(t, h.action_code);
                          const roleTxt = roleLabel(t, h.user_role, { isReview });
                          // W2 (Round W) — strip stored "(role)"
                          // suffix from the user_name before appending
                          // the current-language role label (display
                          // only; stored row unchanged).
                          const rawName = (h.user_name || "").replace(/\s*\([^()]*\)\s*$/, "").trim();
                          subtitle = rawName
                            ? (roleTxt ? `${rawName} (${roleTxt})` : rawName)
                            : (roleTxt || "");
                          const et = h.entity_type || "";
                          const elabel = entityLabel(t, et);
                          let tail = "";
                          if (et === "form" || et === "engagement_forms") {
                            tail = formTail(h, formsIndex, lang);
                          } else if (et === "tcwg_311a_log") {
                            // W3 + X2 (Round W/X) — translate the
                            // stored category slug via
                            // `tcwg.catReview.<slug>` on review
                            // engagements, falling back to
                            // `tcwg.category.<slug>` on audits.
                            // Stored row is unchanged (R6/R7).
                            const desc = h.entity_description || "";
                            const [head, ...rest] = desc.split(" — ");
                            const headOut = tcwgCategoryLabel(t, (head || "").trim(), { isReview });
                            const descOut = rest.length
                              ? `${headOut} — ${rest.join(" — ")}`
                              : (headOut || desc);
                            const ref = h.entity_ref || "";
                            // X1 — the entity label already carries
                            // the ref ("سجل اتصالات 311(a)"), so drop
                            // ref from the tail. Fallback to the W1
                            // desc-starts-with-ref dedupe when the
                            // label doesn't already end with the ref.
                            if (ref && _labelEndsWithRef(elabel, ref)) {
                              tail = descOut;
                            } else if (ref && descOut && descOut.toLowerCase().startsWith(ref.toLowerCase())) {
                              tail = descOut;
                            } else {
                              tail = [ref, descOut].filter(Boolean).join(" ");
                            }
                          } else {
                            const ref = h.entity_ref || "";
                            const desc = h.entity_description || "";
                            tail = (ref && desc && desc.toLowerCase().startsWith(ref.toLowerCase()))
                              ? desc
                              : [ref, desc].filter(Boolean).join(" ");
                          }
                          snippet = tail ? `${elabel} · ${tail}` : elabel;
                        } else {
                          title = lang === "ar" && h.title_ar ? h.title_ar : h.title;   // Batch 27 · Add-on 3 · A: EN / AR titles
                          subtitle = lang === "ar" && h.subtitle_ar ? h.subtitle_ar : h.subtitle;
                          snippet = h.snippet;
                        }
                        return (
                        <li key={i}>
                          <button
                            className="group flex w-full items-start gap-2 border-b px-3 py-2 text-start hover:bg-emerald-50 dark:hover:bg-emerald-950/30 last:border-b-0"
                            onClick={() => goto(h)}
                            data-testid={`search-hit-${key}-${i}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 text-[13px] font-medium">
                                <Highlighted text={title} q={q} />
                                {h.is_not_applicable && (
                                  <span className="rounded bg-slate-200 px-1.5 text-[9px] uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                                    {t("globalSearch.notApplicable")}
                                  </span>
                                )}
                              </div>
                              {subtitle && (
                                <div className="truncate text-[11px] text-muted-foreground" title={subtitle} dir="auto">{/* Batch 27: a Latin date / voucher keeps its order in Arabic */}
                                  <Highlighted text={subtitle} q={q} />
                                </div>
                              )}
                              {snippet && (
                                <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-600 dark:text-slate-400" title={snippet} dir="auto">
                                  <Highlighted text={snippet} q={q} />
                                </div>
                              )}
                              {(h.updated_by || h.updated_at) && (
                                <div className="mt-0.5 text-[10px] text-muted-foreground">
                                  {h.updated_by && <>{t("globalSearch.lastEditedBy")} <b>{h.updated_by}</b>{h.updated_at && " · "}</>}
                                  {h.updated_at && (/^\d{4}-\d{2}-\d{2}$/.test(String(h.updated_at)) ? fmtDate(h.updated_at) : fmtDateTimeFirm(h.updated_at))}
                                </div>
                              )}
                            </div>
                            <ArrowRight className="mt-1 h-3 w-3 flex-none text-muted-foreground transition group-hover:text-emerald-600" />
                          </button>
                          {h.is_audit_trail && h.item_route && (
                            <button type="button" className="mb-1 ms-3 text-[11px] text-emerald-700 underline"
                                    onClick={() => goto(h, true)} data-testid={`search-hit-${key}-${i}-item`}>
                              {t("globalSearch.openItem")}
                            </button>
                          )}
                        </li>
                      );})}
                    </ul>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="flex items-center justify-between border-t bg-slate-50 px-3 py-1.5 text-[10px] text-muted-foreground dark:bg-slate-900/60">
            <span>{t("globalSearch.scopedTo")}</span>
            <span><kbd className="rounded border bg-white px-1 dark:bg-slate-800">{t("globalSearch.esc")}</kbd> {t("globalSearch.toClose")}</span>
          </div>
        </div>
      )}
    </div>
  );
}
